import { useContext } from 'react';
import { useCustomMessage } from '../../hooks';
import { Mutation } from './mutation/index';
import { Query } from './query/index';
import { Tools } from '../../shared';
import axios, { AxiosRequestConfig } from 'axios';
import { BackendError } from '../../shared/BackendError';
import { ContextApp } from '../../contexts/ContextApp';
import { EnumsValues, ObjectStore } from '../../enums/EnumsValues';
import i18n from '../../i18n/i18n';
import ms from 'ms';
import {
  IOfflineData,
  useOfflineFunctions,
} from '../../shared/useOfflineFunctions';

const checkMaintenanceModeAndRedirect = () => {
  if (window.location.pathname !== '/maintenance') {
    window.location.href = '/maintenance';
  }
};

const backendWSUrl: string = import.meta.env.VITE_BACKEND_WS;
console.warn('backendWSUrl');
console.warn(backendWSUrl);
type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const createAuthenticatedRequestConfig = (): AxiosRequestConfig => {
  const token = Tools.getStorage(EnumsValues.LocalStorageKeys.Token);
  return {
    ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
    withCredentials: true,
  };
};

const rmTypenameObj = (obj: object): UnknownRecord => {
  return Object.fromEntries(
    Object.entries(obj).filter(([key]) => key !== '__typename'),
  );
};

const parseObj = (obj: UnknownRecord): UnknownRecord => {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => {
      const numberValue = Number(value);
      if (!Number.isNaN(numberValue)) return [key, numberValue];
      if (value === 'false') return [key, false];
      if (value === 'true') return [key, true];
      return [key, value];
    }),
  );
};

const resultQuery = (data: unknown): Promise<unknown> => {
  return new Promise((resolve, reject) => {
    if ((Array.isArray(data) && data.length === 0) || !data) {
      resolve(data);
      return;
    }
    const response = Array.isArray(data) ? data[0] : data;
    if (!isRecord(response) || typeof response.__typename !== 'string') {
      reject(new BackendError(null, i18n.t('error.invalidResultQuery')));
      return;
    }
    if (response.__typename !== 'ResultError') {
      resolve(data);
      return;
    }
    if (response.status_code === 9009) checkMaintenanceModeAndRedirect();
    reject(
      new BackendError(
        typeof response.status_code === 'string' ||
          typeof response.status_code === 'number'
          ? response.status_code
          : null,
        typeof response.message === 'string' ? response.message : '',
        typeof response.message_translation_key === 'string'
          ? response.message_translation_key
          : undefined,
      ),
    );
  });
};
/**
 * @description Lista de códigos de error
 */
export const ERROR_CODE = {
  SERVER_ERROR: 'SERVER_ERROR',
  AUTHORIZATION_ERROR: 'AUTHORIZATION_ERROR',
  AUTHENTICATION_ERROR: 'AUTHENTICATION_ERROR',
  GENERAL_ERROR: 'GENERAL_ERROR',
};

interface GraphqlErrorPayload {
  status_code?: string | number;
  message?: string;
  message_translation_key?: string;
}

interface GraphqlHttpResponse {
  data?: Record<string, unknown>;
  errors?: GraphqlErrorPayload[];
}

const handleGraphqlErrors = async (
  errors: GraphqlErrorPayload[] | undefined,
  options: IRequestOptions,
  isRetry: boolean,
  setSessionExpired: (expired: boolean) => void,
  retryRequest: (
    requestOptions: IRequestOptions,
    retry?: boolean,
  ) => Promise<unknown>,
): Promise<unknown> => {
  const error = errors?.[0];
  if (!error) return undefined;
  if (error.status_code === 9009) checkMaintenanceModeAndRedirect();

  if (error.status_code === 401) {
    if (isRetry) {
      setSessionExpired(true);
      return undefined;
    }
    const refreshed = await retryRequest(
      { mutation: Mutation.refreshToken },
      true,
    );
    if (isRecord(refreshed) && typeof refreshed.accessToken === 'string') {
      Tools.setStorage(
        EnumsValues.LocalStorageKeys.Token,
        refreshed.accessToken,
      );
    }
    return retryRequest(options, true);
  }

  throw new BackendError(
    error.status_code ?? null,
    error.message ?? '',
    error.message_translation_key,
  );
};

const GraphqlService = () => {
  const { setLoading, setUser, setSessionExpired, user } =
    useContext(ContextApp);
  const { messageModalInfo } = useCustomMessage();
  const { canUseOfflineFunctions, indexedDatabaseRef } = useOfflineFunctions();

  /**
   * @description Lista de códigos de error
   */
  const resolveResultError = (
    resultError: BackendError,
    callback: () => void,
  ) => {
    if (resultError?.status_code) {
      switch (resultError.status_code) {
        case ERROR_CODE.SERVER_ERROR:
          console.error('GraphQL server error:', resultError);
          callback();
          break;
        case ERROR_CODE.AUTHENTICATION_ERROR:
          messageModalInfo({
            context: 'GraphqlService.resolveResultError.authentication',
            message: resultError.message,
          });
          setUser(undefined);
          break;
        case ERROR_CODE.AUTHORIZATION_ERROR:
          callback();
          break;
        case ERROR_CODE.GENERAL_ERROR:
          console.error('GraphQL error:', resultError);
          callback();
          break;
        default:
          console.error('Unexpected GraphQL error:', resultError);
          callback();
          break;
      }
    } else {
      console.error('GraphQL error without status code:', resultError);
      callback();
    }
  };
  const customFileRequest = async (
    options: Omit<IRequestOptions, 'query'> & { mutation: IRequest },
    fileData: IFileData[],
  ) => {
    /* Armamos el form data */
    const formData = new FormData();
    const operations = JSON.stringify({
      operationName: options.mutation.name,
      query: options.mutation.gql,
      variables: options.variables,
    });
    formData.append('operations', operations);
    const map: Record<string, string[]> = {};
    fileData.forEach((file, index) => {
      map[(index + 1).toString()] = [file.path];
    });

    formData.append('map', JSON.stringify(map));

    fileData.forEach((file, index) => {
      formData.append((index + 1).toString(), file.file || 'null');
    });

    /* Armamos el objeto config para axios */
    const axiosConfig: AxiosRequestConfig = {};
    const token = Tools.getStorage(EnumsValues.LocalStorageKeys.Token);
    if (token) {
      axiosConfig.headers = {
        Authorization: `Bearer ${token}`,
      };
    }
    const loading = options.loading !== false;
    if (loading) {
      setLoading(() => true);
    }
    try {
      const res = await axios.post(backendWSUrl, formData, axiosConfig);
      try {
        return await resultQuery(res.data.data[options.mutation.name]);
      } catch (error) {
        resolveResultError(error as BackendError, () => {
          if (loading) {
            setLoading(() => false);
          }
          throw error;
        });
      }
    } catch (err: unknown) {
      if (loading) {
        setLoading(() => false);
      }
      throw err;
    }
    if (loading) {
      setLoading(() => false);
    }
  };

  const transformVariablesToIDBKey = (
    variables: UnknownRecord | undefined,
  ): string => {
    let data: UnknownRecord;
    if (!variables) {
      data = {};
    } else {
      data = structuredClone(variables);
    }

    if (user) {
      data.user_id = user.id;
    }

    return JSON.stringify(data);
  };

  const getDataFromIndexedDB = async ({
    objectStore,
    offlineDataTTL = ms('7d'),
    variables,
  }: {
    objectStore: ObjectStore;
    offlineDataTTL?: number;
    variables?: UnknownRecord;
  }) => {
    if (!canUseOfflineFunctions || !indexedDatabaseRef.current) {
      console.error(
        'Base de datos no inicializada, pongase en contacto con el administrador del sistema',
      );
      return undefined;
    }

    const now = new Date();

    const db = indexedDatabaseRef.current;

    const key = transformVariablesToIDBKey(variables);

    const objectStoreData: IOfflineData | undefined = await db.get(
      objectStore,
      key,
    );

    if (
      !objectStoreData ||
      now.getTime() > objectStoreData.timestamp.getTime() + offlineDataTTL
    ) {
      return undefined;
    }

    return objectStoreData;
  };

  const updateDataInIndexedDB = async ({
    objectStore,
    data,
    variables,
  }: {
    objectStore: ObjectStore;
    data: unknown;
    variables?: UnknownRecord;
  }) => {
    if (!canUseOfflineFunctions || !indexedDatabaseRef.current) {
      console.error(
        'Base de datos no inicializada, pongase en contacto con el administrador del sistema',
      );
      return;
    }

    const db = indexedDatabaseRef.current;
    const key = transformVariablesToIDBKey(variables);

    const dataToInsert: IOfflineData = {
      data,
      key,
      timestamp: new Date(),
    };

    await db.put(objectStore, dataToInsert, key);
  };

  const getOfflineQueryData = async (
    query: IQuery | undefined,
    variables: UnknownRecord | undefined,
  ): Promise<IOfflineData | undefined> => {
    if (!query?.objectStore) return undefined;
    try {
      return await getDataFromIndexedDB({
        objectStore: query.objectStore,
        variables,
        offlineDataTTL: query.offlineDataTTL,
      });
    } catch (error) {
      console.error(error);
      return undefined;
    }
  };

  const handleQueryResult = async (
    data: unknown,
    query: IQuery | undefined,
    variables: UnknownRecord | undefined,
    loading: boolean,
  ): Promise<unknown> => {
    try {
      if (query?.objectStore) {
        await updateDataInIndexedDB({
          objectStore: query.objectStore,
          data,
          variables,
        });
      }
      const result = await resultQuery(data);
      if (loading) setLoading(() => false);
      return result;
    } catch (error) {
      if (loading) setLoading(() => true);
      resolveResultError(error as BackendError, () => {
        if (loading) setLoading(() => false);
        throw error;
      });
      if (loading) setLoading(() => false);
      return undefined;
    }
  };

  const customRequest = async (
    options: IRequestOptions,
    isRetry?: boolean,
  ): Promise<unknown> => {
    const operation = options.query ?? options.mutation;
    if (!operation) {
      throw new Error('Error customRequest: query and mutation is null.');
    }
    const offlineData = await getOfflineQueryData(
      options.query,
      options.variables,
    );
    if (offlineData && !navigator.onLine) {
      return resultQuery(offlineData.data);
    }

    const loading = options.loading !== false;

    if (loading) {
      setLoading(() => true);
    }
    const axiosConfig = createAuthenticatedRequestConfig();
    try {
      const res = await axios.post<GraphqlHttpResponse>(
        backendWSUrl,
        {
          variables: options.variables ?? {},
          operationName: operation.name,
          query: options.query?.gql ?? options.mutation?.gql,
        },
        axiosConfig,
      );

      if (res.data.data) {
        return await handleQueryResult(
          res.data.data[operation.name],
          options.query,
          options.variables,
          loading,
        );
      } else if (res.data.errors?.length) {
        return await handleGraphqlErrors(
          res.data.errors,
          options,
          isRetry ?? false,
          setSessionExpired,
          customRequest,
        );
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        /* Detectamos connection aborted */
        if (error.code === 'ECONNABORTED' && offlineData) {
          return await resultQuery(offlineData.data);
        }
        console.log(error);
      }

      if (loading) {
        setLoading(() => false);
      }
      throw error;
    }
  };
  return {
    Query,
    Mutation,
    rmTypenameObj,
    parseObj,
    customRequest,
    customFileRequest,
  };
};

export default GraphqlService;

export interface IRequest {
  name: string;
  gql: string;
}

export interface IQuery extends IRequest {
  objectStore?: ObjectStore;
  offlineDataTTL?: number;
}

export interface IRequestOptions {
  query?: IQuery;
  mutation?: IRequest;
  variables?: UnknownRecord & {
    input?: object;
    orderBy?: object;
    skip?: number;
    take?: number;
  };
  cache?: boolean;
  loading?: boolean;
}

export interface IFileData {
  file: File | null;
  path: string;
}
